import axios from "axios";
import jwtDecode from "jwt-decode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";

import ErrorHandler from "../../common/ErrorHandler";
import Filters from "../../common/Filters/Filters";
import NewAuctionDashboard from "./NewAuctionDashboard";
import SearchBoxWithCustomQuery from "../../common/SearchBoxWithCustomQuery";
import Table from "../../common/Table/Table";
import { indianCurrencyFormater } from "../../helpers/functions/currencyFormater";
import useAutoFetch from "../../hooks/useAutoFetch";
import { errorToast } from "../../react-toastfiy/toast";
import auctionService from "../../services/auction/auction.service";
import userService from "../../services/user.service";
import Timer from "../../common/Timer";
import useRemainingHeight from "../../hooks/useRemainingHeight";

const SMART_FILTERS_ALLOWED_ROLES = [
  "Auction Super Admin",
  "Developer",
  "Super Admin",
  "Auction CEO",
  "Business Analyst",
];

function AuctionOverview() {
  const history = useHistory();

  const tableRef = useRef(null);
  const [allData, setAllData] = useState([]);
  const [fetchDataF, setFetchData] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [autoRefreshOn, setAutoRefreshOn] = useState(false);
  const [smartFiltersOn, setSmartFiltersOn] = useState(false);
  const [stateHeadStates, setStateHeadStates] = useState([]);
  const [externalSelectedFilters, setExternalSelectedFilters] = useState(null);
  const [newView, setNewView] = useState(false);
  const { height, ref } = useRemainingHeight(20, [loadingData]);

  const currentUser = useMemo(() => {
    try {
      return jwtDecode(localStorage.getItem("refreshToken"));
    } catch (error) {
      return {};
    }
  }, []);
  const canUseSmartFilters = SMART_FILTERS_ALLOWED_ROLES.includes(
    currentUser?.role,
  );

  const filtersList = [
    { key: "state_name", name: "State", id: "state_name" },
    { key: "super_segment", name: "Segment", id: "super_segment" },
    { key: "brand_name", name: "Financier", id: "brand_name" },
    {
      key: "auction_end_date_for_filter",
      name: "End Date",
      id: "auction_end_date_for_filter",
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoadingData(true);

      const form_data = new FormData();
      form_data.append("auction_status", "live");

      const source = axios.CancelToken.source();
      const res = await auctionService.GetLiveAuction(form_data, source);

      if (res?.status === 200) {
        setTimer("30");
        const newRes = res?.data.map((val) => ({
          ...val,
          auction_end_date_for_filter: val?.end_date?.split(" ")[0],
          state: val.state_name,
          segment: val.segment_name,
          auction_end_time: val?.end_date,
          participating_percentage:
            val?.subscribed_users_count > 0
              ? (
                  (val?.participating_bidders_count /
                    val?.subscribed_users_count) *
                  100
                ).toFixed(2)
              : 0,
        }));

        setLoadingData(false);
        setFilteredData(newRes);
        setAllData(newRes);
      } else {
        setLoadingData(false);
        setFilteredData([]);
        setAllData([]);
        errorToast(res?.msg);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  }, []);

  const { timer, loading, setTimer } = useAutoFetch(
    fetchData,
    30000,
    autoRefreshOn,
  );

  useEffect(() => {
    if (!canUseSmartFilters || !currentUser?.phone) return;

    const form_data = new FormData();
    form_data.append("phone", currentUser.phone);

    userService.GetStatesWithStateHeads(form_data).then((res) => {
      if (res?.status === 200) {
        setStateHeadStates(res?.data || []);
      }
    });
  }, [canUseSmartFilters, currentUser]);

  useEffect(() => {
    if (smartFiltersOn) {
      if (stateHeadStates.length > 0) {
        setExternalSelectedFilters({
          state_name: Object.fromEntries(
            stateHeadStates.map((state) => [state, true]),
          ),
        });
      }
    } else {
      setExternalSelectedFilters((prev) => (prev ? { state_name: {} } : null));
    }
  }, [smartFiltersOn, stateHeadStates]);

  const handleRedirect = (key, value) => {
    let assetDashboardPath = `/dashboard/live_dashboard/live/assets_dashboard`;

    if (
      ["Developer", "Super Admin", "Business Analyst"].includes(
        currentUser?.role,
      )
    ) {
      assetDashboardPath = `/live_auction_dashboard`;
    }

    if (key === "auction_id") {
      history.push({
        pathname: assetDashboardPath,
        search: `?auction_id=${value?.auction_id}`,
        state: { filteredData: value, flag: "auction_id" },
      });
    } else if (key === "bidded_asset_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?bidded_asset_count=${value?.auction_id}`,
        state: { filteredData: value, flag: "bidded_asset_count" },
      });
    } else if (key === "asset_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?asset_count=${value?.auction_id}`,
        state: { filteredData: value, flag: "asset_count" },
      });
    } else if (key === "participating_bidders_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?participating_bidders_count=${value?.auction_id}`,
        state: { filteredData: value, flag: "participating_bidders_count" },
      });
    } else if (key === "nonparticipating_bidders_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?nonparticipating_bidders_count=${value?.auction_id}`,
        state: { filteredData: value, flag: "nonparticipating_bidders_count" },
      });
    }
  };

  const table_columns = [
    {
      title: "#",
      data_key: "",
      render: (row, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: "Auction Id",
      data_key: "auction_id",
      sortable: true,
      render: (row) => {
        return (
          <>
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() => handleRedirect("auction_id", row)}
            >
              {row?.auction_id}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Auction end time",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className="text-nowrap"
            >
              <Timer targetDateString={row?.end_date} />
              {/* {row?.remainingTime} */}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "State",
      data_key: "state_name",
      sortable: false,
    },
    {
      title: "Segment",
      data_key: "super_segment",
      sortable: false,
    },
    {
      title: "Financier",
      data_key: "brand_name",
      sortable: false,
    },
    {
      title: "No of Assets",
      data_key: "asset_count",
      sortable: false,
    },
    {
      title: "Assets Bidded",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            {row?.bidded_asset_count !== 0 ? (
              <span
                style={{ cursor: "pointer" }}
                className=" text-nowrap link-primary text-decoration-none"
                onClick={() => handleRedirect("bidded_asset_count", row)}
              >
                {row?.bidded_asset_count}
              </span>
            ) : (
              <span className=" text-nowrap  text-decoration-none">
                {row?.bidded_asset_count}
              </span>
            )}
            /
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() => handleRedirect("asset_count", row)}
            >
              {row?.asset_count}
            </span>
          </>
        );
      },
    },
    {
      title: "Avg. Selling Price",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span className=" text-nowrap  text-decoration-none">
              {indianCurrencyFormater(row?.average_selling_price)}
            </span>
          </>
        );
      },
    },
    {
      title: "Bidders Participated",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            {row?.participating_bidders_count !== 0 ? (
              <span
                style={{ cursor: "pointer" }}
                className=" text-nowrap link-primary text-decoration-none"
                onClick={() =>
                  handleRedirect("participating_bidders_count", row)
                }
              >
                {row?.participating_bidders_count}
              </span>
            ) : (
              <span className=" text-nowrap  text-decoration-none">
                {row?.participating_bidders_count}
              </span>
            )}
            /
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() =>
                handleRedirect("nonparticipating_bidders_count", row)
              }
            >
              {row?.subscribed_users_count}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Bids Per Asset",
      data_key: "bids_per_asset",
      sortable: true,
    },
    {
      title: "Total Bids",
      data_key: "total_bids_count",
      sortable: true,
    },
    {
      title: "Participation Percentage",
      data_key: "participating_percentage",
      sortable: true,
      render: (row) => {
        return (
          <>
            <span className=" text-nowrap  text-decoration-none">
              {row?.participating_percentage}%
            </span>
          </>
        );
      },
    },
  ];

  const handleDownloadExcel = (e, value) => {
    e.preventDefault();

    if (Object.entries(value)?.length > 0) {
      const data = [];
      const columns = [
        "Sr. No.",
        "Auction Id",
        "State",
        "Segment",
        "Financier",
        "No of Assets",
        "Assets Bidded",
        "Avg. Selling Price",
        "Bidders Participated",
        "Bids Per Asset",
        "Total Bids",
        "Participation Percentage",
        "Target",
      ];
      data.push(columns);

      let serialNumber = 1;
      let totalAssetsBidded = 0;
      let totalAssetsCount = 0;

      let stateWiseBidders = {};
      let stateWiseSubscribedUsers = {};

      let total_auctions = 0;
      let total_participating_percentage = 0;

      Object.entries(value).forEach(([row, item]) => {
        const biddedAssets = Number(item?.bidded_asset_count) || 0;
        const assetCount = Number(item?.asset_count) || 0;
        const biddersParticipated =
          Number(item?.participating_bidders_count) || 0;
        const subscribedUsers = Number(item?.subscribed_users_count) || 0;

        totalAssetsBidded += biddedAssets;
        totalAssetsCount += assetCount;

        if (!stateWiseBidders[item?.state]) {
          stateWiseBidders[item?.state] = biddersParticipated;
        }

        if (!stateWiseSubscribedUsers[item?.state]) {
          stateWiseSubscribedUsers[item?.state] = subscribedUsers;
        }

        if (Number(item?.participating_percentage) > 0) {
          total_auctions += 1;
          total_participating_percentage +=
            Number(item?.participating_percentage) || 0;
        }

        const rowData = [
          serialNumber++,
          item?.auction_id || "-",
          item?.state || "-",
          item?.segment || "-",
          item?.brand_name || "-",
          assetCount || "-",
          `${biddedAssets} / ${assetCount}` || "-",
          item?.average_selling_price || "-",
          `${biddersParticipated} / ${subscribedUsers}` || "-",
          item?.bids_per_asset || "-",
          item?.total_bids_count || "-",
          Number(item?.participating_percentage) || 0,
          "",
        ];
        data.push(rowData);
      });
      const avg_participating_percentage = (
        total_auctions > 0 ? total_participating_percentage / total_auctions : 0
      ).toFixed(2);

      const totalBiddersParticipated = Object.values(stateWiseBidders).reduce(
        (acc, count) => acc + count,
        0,
      );

      const totalSubscribedUsers = Object.values(
        stateWiseSubscribedUsers,
      ).reduce((acc, count) => acc + count, 0);

      const totalRow = [
        "Total",
        "-",
        "-",
        "-",
        "-",
        totalAssetsCount,
        `${totalAssetsBidded} / ${totalAssetsCount}`,
        "-",
        `${totalBiddersParticipated} / ${totalSubscribedUsers}`,
        "-",
        "-",
        avg_participating_percentage,
        "",
      ];
      data.push(totalRow);

      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Auction overview");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const dataBlob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(dataBlob);
      link.download = "Live Auction Overview.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    }
  };

  try {
    if (newView)
      return (
        <NewAuctionDashboard mode="live" onBack={() => setNewView(false)} />
      );
    return (
      <>
        <div className=" mt-2">
          <div
            class=""
            // style={{ backgroundColor: "#e6f9ff" }}
          >
            <div className="d-flex justify-content-between align-items-baseline flex-md-row flex-column flex-wrap gap-2  my-2">
              <div className="h5 align-items-start">
                <span className="fw-bold">Live Auction Overview</span>
              </div>

              <div className="d-flex justify-content-end gap-2 align-items-end flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success align-self-center"
                  onClick={() => setNewView(true)}
                >
                  ✨ Try the new view
                </button>
                <div className="d-flex align-items-center gap-1">
                  <label
                    htmlFor="autoRefreshSwitch"
                    className="text-muted  cursor-pointer"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Auto Refresh
                  </label>
                  <div className="form-check form-switch mt-2 ps-0">
                    <input
                      className="form-check-input m-0 cursor-pointer"
                      type="checkbox"
                      id="autoRefreshSwitch"
                      aria-label="Toggle auto refresh"
                      checked={autoRefreshOn}
                      onChange={() => setAutoRefreshOn((prev) => !prev)}
                    />
                  </div>
                </div>
                {canUseSmartFilters && (
                  <div className="d-flex align-items-center gap-1">
                    <label
                      htmlFor="smartFiltersSwitch"
                      className="text-muted cursor-pointer"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Smart Filters
                    </label>
                    <div className="form-check form-switch mt-2 ps-0">
                      <input
                        className="form-check-input m-0 cursor-pointer"
                        type="checkbox"
                        id="smartFiltersSwitch"
                        aria-label="Toggle smart filters"
                        checked={smartFiltersOn}
                        disabled={stateHeadStates.length === 0}
                        onChange={() => setSmartFiltersOn((prev) => !prev)}
                      />
                    </div>
                  </div>
                )}
                <SearchBoxWithCustomQuery
                  allData={allData}
                  setFilteredData={setFilteredData}
                  placeholder="Search..."
                  return_array={true}
                  keysToSearchOn={[
                    "auction_id",
                    "state_name",
                    "segment_name",
                    "brand_name",
                  ]}
                />
                <Filters
                  data={allData}
                  setData={setFilteredData}
                  filteredData={filteredData}
                  setFilteredData={setFilteredData}
                  filtersList={filtersList}
                  localStorageKey="live_auction_overview_filters"
                  externalSelectedFilters={externalSelectedFilters}
                />
                {filteredData && filteredData.length > 0 && (
                  <button
                    className=" btn bg-danger-subtle text-danger"
                    style={{
                      height: "40px",
                      width: "40px",
                      borderRadius: "40px",
                    }}
                    onClick={(e) => {
                      handleDownloadExcel(e, filteredData);
                    }}
                  >
                    <i className="bi bi-download"></i>
                  </button>
                )}
                <div>
                  <div class="input-group flex-nowrap mb-0">
                    <button
                      class=" btn bg-danger-subtle text-danger "
                      id="basic-addon1"
                      onClick={() => {
                        setFetchData((prev) => !prev);
                        fetchData();
                      }}
                      disabled={loading || loadingData}
                    >
                      {loading || loadingData ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            aria-hidden="true"
                            style={{ fontSize: "10px" }}
                          ></span>
                          <span className="visually-hidden">Loading...</span>
                        </>
                      ) : (
                        <i className="bi bi-arrow-clockwise"></i>
                      )}
                    </button>
                    <button
                      class="btn btn-outline-danger "
                      type="button"
                      disabled
                    >
                      {!autoRefreshOn
                        ? "Off"
                        : !loading || loadingData
                          ? timer
                          : "-"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: "0.85rem",
                color: "#666",
                fontWeight: "400",
                marginTop: "4px",
              }}
            >
              Real-time view of all currently running auctions with bidder
              participation and asset bidding stats
            </div>
            <div ref={ref}>
              <Table
                columns={table_columns.filter((column) => column)}
                data={filteredData}
                loading={loadingData}
                no_data_msg="No data"
                // td_text_position="left"
                td_class="text-capitalize"
                td_style={{ fontSize: "0.9rem" }}
                th_text_position="start"
                td_text_position="start"
                loading_cols={table_columns.filter((column) => column).length}
                loading_rows={5}
                flag="OverView"
                maxTableHeight={`${height}px`}
              />
            </div>
          </div>
        </div>
      </>
    );
  } catch (error) {
    console.log(error);
    return <ErrorHandler error={error} />;
  }
}

export default AuctionOverview;
